/**
 * Application state — Preact signals.
 *
 * Kept deliberately flat: the session state of a mail client is a handful of
 * selections plus per-folder message pages. There is no global immutable
 * tree to reconcile; each signal owns one concern.
 */
import { signal } from "@preact/signals";

import type { ParsedMessage } from "../engine/types.ts";

/**
 * Lifecycle of the account connection.
 * - `booting`: the WASM engine is still loading.
 * - `no-account`: nothing has been configured on this device yet.
 * - `connected`: talking to the IMAP/SMTP bridge.
 * - `error`: the bridge (or engine) reported a fatal error.
 */
export type ConnectionState = "booting" | "no-account" | "connected" | "error";

/**
 * One configured mail account, as the UI may see it. Credentials stay in the
 * storage module; a summary never carries secret material.
 */
export interface AccountSummary {
  /**
  Stable local identifier (the lowercased email address).
   */
  readonly id: string;
  /**
  User-chosen display label.
   */
  readonly label: string;
  /**
  Primary email address.
   */
  readonly email: string;
}

/**
A folder as shown in the sidebar.
 */
export interface FolderSummary {
  /**
  Server folder id.
   */
  readonly id: string;
  /**
  Display name.
   */
  readonly name: string;
  /**
  Unread count, when known.
   */
  readonly unread: number;
}

/**
One row of the message list: the fields a list view needs, nothing more.
 */
export interface MessageListItem {
  /**
  Message UID (stable within the folder's `uidValidity`).
   */
  readonly id: number;
  /**
  Sender display string, already resolved from the parsed address list.
   */
  readonly from: string;
  /**
  Decoded subject; `null` when the message had none.
   */
  readonly subject: string | null;
  /**
  Message date as unix seconds; `null` when unparseable.
   */
  readonly date: number | null;
  /**
  Seen flag from the server.
   */
  readonly unread: boolean;
  /**
  Flagged (`\Flagged`) status from the server.
   */
  readonly flagged: boolean;
  /**
  Message size in bytes (`RFC822.SIZE`).
   */
  readonly size: number;
}

/**
Currently selected items across the three panes.
 */
export const connection = signal<ConnectionState>("booting");
export const accounts = signal<readonly AccountSummary[]>([]);
export const activeAccountId = signal<string | null>(null);
export const folders = signal<readonly FolderSummary[]>([]);
export const activeFolderId = signal<string | null>(null);
export const activeMessageId = signal<number | null>(null);

/**
Rows of the active folder's current page.
 */
export const messages = signal<readonly MessageListItem[]>([]);

/**
The parsed message behind `activeMessageId`, once fetched.
 */
export const activeMessage = signal<ParsedMessage | null>(null);

/**
Per-pane busy flags: sidebar, list, reader and composer each say what they
are waiting on, so one slow call never blanks the whole app.
 */
export const foldersLoading = signal<boolean>(false);
export const listLoading = signal<boolean>(false);
export const messageLoading = signal<boolean>(false);
export const sending = signal<boolean>(false);

/**
User-facing failure notes per pane; `null` means "nothing to report".
 */
export const folderError = signal<string | null>(null);
export const listError = signal<string | null>(null);
export const messageError = signal<string | null>(null);
export const sendError = signal<string | null>(null);

/**
True while the list shows search results instead of the folder's page.
 */
export const searchMode = signal<boolean>(false);

/**
True when the folder's oldest page has been reached (no "load older").
 */
export const endOfMailbox = signal<boolean>(true);

/**
Number of messages queued in the outbox for later delivery.
 */
export const queuedCount = signal<number>(0);

/**
A pending composer opening: a fresh message, or a reply to a UID.
 */
export const composeRequest = signal<
  | null
  | { readonly mode: "new" }
  | { readonly mode: "reply"; readonly uid: number }
>(null);

/**
 * Selects a message for the reader pane; a parsed message is dropped with
 * the selection so the reader never shows a stale body.
 *
 * @param id - Message identifier, or `null` to close the reader.
 */
export function selectMessage(id: number | null): void {
  activeMessageId.value = id;
  activeMessage.value = null;
  messageError.value = null;
}

/**
 * Switches the active account and clears dependent selections.
 *
 * @param accountId - The account to activate.
 */
export function selectAccount(accountId: string): void {
  activeAccountId.value = accountId;
  activeFolderId.value = null;
  activeMessageId.value = null;
  activeMessage.value = null;
  folders.value = [];
  messages.value = [];
  folderError.value = null;
  listError.value = null;
  messageError.value = null;
  searchMode.value = false;
  endOfMailbox.value = true;
}

/**
 * Clears every per-mailbox signal back to folder-selection state.
 */
export function resetFolderView(): void {
  activeMessageId.value = null;
  activeMessage.value = null;
  messages.value = [];
  listError.value = null;
  messageError.value = null;
  searchMode.value = false;
  endOfMailbox.value = true;
}
